import { getNumberOrZero } from '@/common/utils';
import { CompactButton } from '@/components/buttons';
import { StandardButton } from '@/components/buttons/standard';
import { useCurrentUser } from '@/hooks';
import { ContentWithLoad } from '@/layouts/content-with-load';
import { PrimaryLayout } from '@/layouts/primary';
import { Carousel } from '@mantine/carousel';
import { Alert, Flex, Text } from '@mantine/core';
import { IconAlertCircle, IconHelpCircle } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { useScanEventsCountForUserQuery } from './graphql.generated';
import { ScanBlob } from './scan-blob';
import { ScanBlobOverlapped } from './scan-blob-overlapped';
import { ScanBlobPlastic } from './scan-blob-plastic';
import styles from './styles.module.css';

export const ScanBagPage = () => {
	const navigate = useNavigate();
	const { currentUser } = useCurrentUser();
	const { data, loading } = useScanEventsCountForUserQuery({
		variables: {
			userId: currentUser?.id ?? '',
		},
		skip: !currentUser?.id,
	});
	const scanCodeButton = useMemo(
		() => <StandardButton onClick={() => navigate({ to: '/scan-code' })}>Scan Code</StandardButton>,
		[navigate]
	);
	const enterCodeButton = useMemo(
		() => (
			<StandardButton variant="light" onClick={() => navigate({ to: '/enter-code' })}>
				Enter Code
			</StandardButton>
		),
		[navigate]
	);

	const helpButton = useMemo(
		() => (
			<CompactButton leftSection={<IconHelpCircle />} onClick={() => navigate({ to: '/help' })}>
				Help
			</CompactButton>
		),
		[]
	);

	/** Take user to /scan-code if user has already scanned codes before */
	useEffect(() => {
		if (loading || !data) return;
		if (getNumberOrZero(data.scanEvents_aggregate?.count) > 0) {
			navigate({ to: '/scan-code' });
		}
	}, [data, loading, navigate]);

	return (
		<PrimaryLayout
			header={{ title: 'Scan a Bag', backTo: '/home', children: helpButton }}
			footer={{ children: [scanCodeButton, enterCodeButton] }}
		>
			<ContentWithLoad loading={loading}>
				<Carousel withIndicators withControls={false} height="100%" classNames={styles}>
					<Carousel.Slide>
						<Flex direction="column" align="center" justify="center" h="100%" gap="md" p="lg">
							<ScanBlobPlastic />
							<Text size="sm" fw="bold">
								Fill the bag with clean soft plastics only.
							</Text>
							<Alert
								icon={<IconAlertCircle />}
								title="DO NOT put these in the bag"
								variant="filled"
								bg="red.0"
							>
								<Text size="sm">Plastic bottles • plastic containers</Text>
								<Text size="sm">rigid plastics • tin cans • glass • rubber</Text>
								<Text size="sm">paper & cardboard • toys • food waste</Text>
							</Alert>
						</Flex>
					</Carousel.Slide>
					<Carousel.Slide>
						<Flex direction="column" align="center" justify="center" h="100%" gap="md" p="xl">
							<ScanBlob />
							<Text size="sm" fw="bold" ta="center">
								When the bag is FULL (as possible), compress it and tightly tie with a double knot.
							</Text>
						</Flex>
					</Carousel.Slide>
					<Carousel.Slide>
						{' '}
						<Flex direction="column" align="center" justify="center" h="100%" gap="md" p="xl">
							<ScanBlobOverlapped />
							<Text size="sm" fw="bold" ta="center">
								Scan the QR code on the CurbyTag.
							</Text>
							<Text size="sm" ta="center">
								(We'll use your device camera)
							</Text>
						</Flex>
					</Carousel.Slide>
				</Carousel>
			</ContentWithLoad>
		</PrimaryLayout>
	);
};
